<template>
  <NuxtLayout name="default">
    <section class="near-me">
      <translate tag="h1"> Book Caravan & holiday parks near me </translate>
      <translate tag="h2" class="subhead">
        Use our caravan and holiday parks near me tool to search for sites by
        proximity to where you are now.
      </translate>

      <span class="page-content">
        <base-submit-button
          class="primary button near-me-btn"
          :disabled="geolocationError"
          :submitting="isGeolocationLoading"
          @click="geolocationMachine.send('LOCATE')"
        >
          <template #submittingText>
            <translate>Fetching your location...</translate>
          </template>
          <img
            src="@/assets/images/map-pin.svg"
            width="41"
            height="41"
            alt=""
          />
          <translate>Show caravan parks near me</translate>
        </base-submit-button>

        <geolocation-error
          v-if="isGeolocationFailed"
          :geolocation-error="geolocationError"
        />
        <div class="near-me-img-wrapper">
          <img
            src="@/assets/near-me/caravans-near-me.jpg"
            srcset="
              ~/assets/near-me/caravans-near-me.jpg        720w,
              ~/assets/near-me/caravans-near-me-large.jpg 2000w
            "
            sizes="100vw"
            :alt="$t('Caravan parks near me')"
            width="360"
            height="240"
            fetchpriority="high"
            decoding="sync"
          />
        </div>

        <translate tag="p">
          Get a change of scenery without breaking the bank by hiring a caravan
          in one of your local caravan parks today. Caravan or holiday parks
          make for a great holiday. With many being open all year round, you
          don't have to worry about the weather when planning your experience.
        </translate>
        <translate tag="p">
          If you are unsure about camping, have never stayed on a campsite
          before or only have a short period of time for your break, we
          recommend renting a static caravan at your local caravan park first.
          This will let you experience the feel of community on the site, enjoy
          the change of scenery and reduce the hassle of having to pitch a tent
          or organise your bedding as most caravans come equipped with all your
          home comforts.
        </translate>
        <translate tag="p" :tt="{ within: defaultDistance }">
          To find what caravan and holiday parks are available near you, click
          on the button below, and grant us permission to use your location.
          We'll show you all the options to hire a caravan at a caravan park
          located within {within, distance, long} of you (as the crow flies).
          You can then filter these results using more than 80 criteria to find
          the perfect site and caravan for you, whether that be dog-friendly,
          have evening entertainment or be located remotely in the depths of the
          countryside.
        </translate>
      </span>

      <section class="near-me-page-links">
        <translate tag="h2"> Other popular searches </translate>

        <nuxt-link :to="$puLocalePath({ name: 'campsites-near-me' })">
          <translate>Camping near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'glamping-near-me' })">
          <translate>Glamping near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'tourers-near-me' })">
          <translate>Touring caravan sites near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'lodges-near-me' })">
          <translate>Lodges near me</translate>
        </nuxt-link>
      </section>

      <nav class="breadcrumbs">
        <span class="breadcrumbs-generator">
          <a
            :href="`https://${$config.public.envUrl}/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }`"
          >
            <translate>Home</translate> </a
          ><span class="crumb">&rsaquo;</span>
          <a
            :href="`https://${$config.public.envUrl}/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }caravans/`"
          >
            <translate>Caravan parks</translate> </a
          ><span class="crumb">&rsaquo;</span>
          <translate>Caravan parks near me</translate>
        </span>
      </nav>
      <nuxt-to-meta :nuxt2meta="head" />
    </section>
  </NuxtLayout>
</template>

<script setup lang="ts">
import generateNearMeUrl from '~/utility/generateNearMeUrl'
import { Distance } from '~/utility/geo/Distance'
import {
  GeolocationContext,
  GeolocationState,
  getGeolocationMachine,
} from '~/utility/geolocation/machine'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'
import { computed, ref, watch, onMounted } from 'vue'

definePageMeta({
  layout: false,
})

const imageGlob = import.meta.glob<{ default: string }>(
  '@/assets/near-me/*.jpg',
  {
    query: '?url',
    eager: true,
  },
)

// Get Nuxt app context
const { $t, $config, $puLocalePath, $i18n } = useNuxtApp()
const route = useRoute()
const router = useRouter()

// Geolocation machine state
const geolocationMachine = ref<ReturnType<typeof getGeolocationMachine>>()
const geolocationContext = ref<GeolocationContext | null>(null)
const geolocationState = ref<GeolocationState | null>(null)

// Computed properties
const _breadcrumbs = computed(() => [{ label: $t('Caravan parks near me') }])

const defaultDistance = computed(() =>
  Distance.new({ mi: $config.public.defaultNearMeWithinRange }),
)

const isGeolocationFailed = computed(
  () => geolocationState.value?.value === 'error',
)

const isGeolocationLoading = computed(
  () => geolocationState.value?.value === 'locating',
)

const geolocationError = computed(() => geolocationContext.value?.errorStatus)

const head = computed(() => ({
  title:
    $t('Caravan & holiday parks with caravans to hire near me') +
    ' | Pitchup.com',
  meta: [
    {
      hid: 'description',
      name: 'description',
      property: 'og:description',
      content: $t(
        'Find and book caravan parks near you. With over 80 filters, you can find the perfect holiday park with static caravans available to hire in your local area in seconds on Pitchup.com.',
      ),
    },
  ],
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: imageGlob['/assets/near-me/caravans-near-me.jpg'].default,
      imagesrcset: `${imageGlob['/assets/near-me/caravans-near-me.jpg'].default} 720w, ${imageGlob['/assets/near-me/caravans-near-me-large.jpg'].default} 2000w`,
      imagesizes: '100vw',
      fetchpriority: 'high',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        name: $t('Caravan parks near me'),
        contentUrl: `https://${$config.public.envUrl}${imageGlob['/assets/near-me/caravans-near-me-large.jpg'].default}`,
      },
    },
    {
      type: 'application/ld+json',
      body: true,
      json: SCHEMA_ORGANIZATION,
      hid: 'schema-organization',
    },
    breadcrumbList.value,
  ],
}))

const breadcrumbList = computed(() => {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `https://${$config.public.envUrl}/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }`,
          name: 'Pitchup.com',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `https://${$config.public.envUrl}/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }caravans/`,
          name: $t('Caravan parks'),
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@id': `https://${$config.public.envUrl}${route.path}`,
          name: $t('Caravan parks near me'),
        },
      },
    ],
  }
  return {
    type: 'application/ld+json',
    body: true,
    json,
  }
})

// Functions
const startGeolocationMachine = () => {
  geolocationMachine.value = getGeolocationMachine()
    .onTransition((state) => {
      geolocationState.value = state
      geolocationContext.value = state.context
    })
    .start()
}

const redirectToSearch = () => {
  if (!geolocationContext.value?.point) {
    return
  }
  const url = generateNearMeUrl(
    'caravans',
    route.query,
    geolocationContext.value.point,
    $i18n.locale,
  )

  void router.push({
    path: $puLocalePath(url.path),
    query: url.query as Record<string, any>,
  })
}

// Lifecycle
onMounted(async () => {
  // eslint-disable-next-line @typescript-eslint/await-thenable -- startGeolocationMachine may be async in some contexts
  await startGeolocationMachine()
})

// Watchers
watch(geolocationState, (newState) => {
  if (newState?.value === 'success') {
    redirectToSearch()
  }
})
</script>
<style>
@import url('@/assets/css/near-me.css');
</style>
